/**
 * Application entry.
 *
 * Order matters: translations resolve before the first render, so no screen ever paints a raw
 * key. The theme class is NOT applied here — `public/theme-init.js` already put it on the root
 * element before the browser had any CSS to paint with (see that file). `startThemeSync()` only
 * takes over from that point: later preference changes, and the OS flipping
 * `prefers-color-scheme` while the preference is `system`.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CSPProvider } from '@base-ui/react/csp-provider';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';

import {
  discardUnsavedPlansOf,
  dropUnsavedPlanFromTab,
  startUnsavedPlanPersistence,
} from '~/entities/plan-draft';
import { onSessionEnd, onSignOut, readSession, startAuthSession } from '~/entities/session';
import { startThemeSync } from '~/shared/design-system/theme';
import { initI18n } from '~/shared/i18n';
import { initCrashReporting } from '~/shared/lib/crashReporting';
import { queryClient } from '~/shared/lib/queryClient';

import '~/shared/design-system/tokens/index.css';
/**
 * The Tailwind utility layer the vendored bklit charts are authored in, plus the bridge that
 * points their `--chart-*` variables at KANON's semantic aliases. Loaded after the tokens,
 * because the bridge is written in terms of them.
 *
 * Global, and honestly so: Tailwind's output is one stylesheet, so what is scoped is which files
 * are SCANNED for classes (`src/shared/vendor/` only), not which files the CSS reaches. A utility
 * class used outside the vendor directory generates nothing and therefore does nothing, which is
 * the fence — but the rule that our own components stay on CSS Modules is a rule, not a lock.
 */
import '~/shared/vendor/vendor.css';

import { startBrandColorPrefetch } from './lib/brandPaint';
import { SessionResolvingScreen } from './ui/SessionResolvingScreen';
import { routeTree } from './routeTree.gen';

// First, before any other module runs code that could throw — a crash before this line is
// unreported by definition. A no-op without VITE_SENTRY_DSN (dev builds by default).
initCrashReporting();

/**
 * Before the session starts resolving, so the brand colour's read is registered to fly with the
 * very first `/auth/me` (see `app/lib/brandPaint.ts`).
 */
startBrandColorPrefetch();

/**
 * Before the router, on purpose. `startAuthSession()` installs the API transport's dependencies
 * (`configureApi`) and starts resolving the persisted session; the root route's `beforeLoad` awaits
 * that same single-flight promise, so a route can only run once the transport is able to serve it.
 */
startAuthSession();

/**
 * The plan builder's crash recovery (`entities/plan-draft/lib/unsavedPlanRecord.ts`): an unsaved
 * new plan is mirrored to this device under the signed-in trainer's id. EVERY session end takes it
 * out of this tab, so the next trainer to sign in here is never offered it; the device copy stays
 * for the same trainer's return. A deliberate sign-out takes the device copy too. Composed here
 * because it is the one place allowed to know both entities — the plan document may not import the
 * session.
 */
startUnsavedPlanPersistence(() => readSession().user?.id ?? null);
onSessionEnd(() => {
  dropUnsavedPlanFromTab();
});
onSignOut(() => {
  discardUnsavedPlansOf(readSession().user?.id ?? null);
});

const router = createRouter({
  routeTree,
  /**
   * The session-resolution boundary's visible half. It appears only if resolution outlasts
   * `defaultPendingMs`, which a cold start with nothing in storage never does — that case settles
   * with no network call at all.
   */
  defaultPendingComponent: SessionResolvingScreen,
  defaultPendingMs: 300,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('index.html is missing its #root element — the app has nowhere to mount.');
}

startThemeSync();

const i18n = await initI18n();
document.documentElement.lang = i18n.language;

/**
 * `disableStyleElements` stops Base UI injecting any inline `<style>` element, which the
 * production CSP's `style-src-elem 'self'` blocks outright.
 *
 * The trap this closes is that the failure is invisible in development. `vite.config.ts` mints a
 * per-process nonce and puts it on `style-src-elem` for the dev server only — a production build
 * carries no nonce, deliberately, because a nonce baked into a static file is no better than
 * `'unsafe-inline'`. So an injected stylesheet works all the way through `npm run dev` and is
 * refused the first time the app is served from `dist/`. `e2e/shell-csp.spec.ts` runs against
 * `vite preview` for exactly that reason.
 *
 * The cost is that Base UI supplies no CSS at all and every visual byte of every overlay is
 * ours — which the foundation ADR §4 requires anyway, so this is a cost already agreed to rather
 * than one this line introduces. Positioning is unaffected: Base UI positions through inline
 * `style` attributes, which `style-src-attr 'unsafe-inline'` permits.
 *
 * It sits ABOVE the router rather than beside a component, because the value has to be in
 * context before the first popup mounts, and popups mount from routed screens.
 */
createRoot(rootElement).render(
  <StrictMode>
    <CSPProvider disableStyleElements>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </CSPProvider>
  </StrictMode>,
);
